// src/features/streams/components/simulcast/DestinationCard.tsx

import {
  Card,
  Group,
  Stack,
  Text,
  Box,
  Alert,
  Menu,
  ActionIcon,
  Button,
  Switch,
  Loader,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconDots,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { YouTubeLogo, FacebookLogo, RtmpLogo } from './PlatformLogos';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import {
  SimulcastType,
  SimulcastStatusType,
  SimulcastErrorAction,
  type ISimulcastAllData,
  type IExtPlatAuthView,
} from '@/lib/types/Simulcast';
import { SimulcastErrorUtils, type DestinationErrorState } from '../../simulcast/simulcastError';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';

export type PendingAction = 'start' | 'stop' | 'delete' | null;

interface DestinationCardProps {
  simulcast: ISimulcastAllData;
  extPlatAuth?: IExtPlatAuthView;
  authsLoaded: boolean;
  pendingAction: PendingAction;
  onStart: (simulcast: ISimulcastAllData) => void;
  onStop: (simulcast: ISimulcastAllData) => void;
  onDelete: (simulcast: ISimulcastAllData) => void;
  onEdit: (simulcast: ISimulcastAllData) => void;
  onReconnect: (simulcast: ISimulcastAllData) => void;
}

/** Brand color backgrounds for platform logos. */
const PLATFORM_BG: Record<string, string> = {
  [SimulcastType.Facebook]: 'var(--mantine-color-blue-0)',
  [SimulcastType.Youtube]: 'var(--mantine-color-red-0)',
};

/** Platform logo with subtle brand-color container. */
function PlatformIcon({ type }: { type: SimulcastType }) {
  const logo =
    type === SimulcastType.Facebook ? <FacebookLogo size={22} /> :
    type === SimulcastType.Youtube ? <YouTubeLogo size={22} /> :
    <RtmpLogo size={22} />;

  return (
    <Box
      w={38}
      h={38}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: PLATFORM_BG[type] ?? 'var(--mantine-color-gray-0)',
        flexShrink: 0,
      }}
    >
      {logo}
    </Box>
  );
}

/** Error banner with action buttons per error type. */
function ErrorBanner({
  error,
  simulcast,
  pendingAction,
  onReconnect,
  onDelete,
}: {
  error: DestinationErrorState;
  simulcast: ISimulcastAllData;
  pendingAction: PendingAction;
  onReconnect: (simulcast: ISimulcastAllData) => void;
  onDelete: (simulcast: ISimulcastAllData) => void;
}) {
  const color = error.action === SimulcastErrorAction.Delete ? 'red' : 'orange';
  const isPending = pendingAction !== null;

  return (
    <Alert
      color={color}
      icon={<IconAlertTriangle size={16} />}
      py="xs"
      px="sm"
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text size="sm" style={{ flex: 1 }}>
          {error.message}
        </Text>
        {error.action === SimulcastErrorAction.Reconnect && (
          <Button
            size="xs"
            variant="light"
            color="orange"
            disabled={isPending}
            onClick={() => onReconnect(simulcast)}
          >
            Reconnect
          </Button>
        )}
        {error.action === SimulcastErrorAction.Delete && (
          <Button
            size="xs"
            variant="light"
            color="red"
            disabled={isPending}
            loading={pendingAction === 'delete'}
            onClick={() => onDelete(simulcast)}
          >
            Delete
          </Button>
        )}
      </Group>
    </Alert>
  );
}

export function DestinationCard({
  simulcast,
  extPlatAuth,
  authsLoaded,
  pendingAction,
  onStart,
  onStop,
  onDelete,
  onEdit,
  onReconnect,
}: DestinationCardProps) {
  const { core } = simulcast;
  const label = SimulcastTransforms.getDestinationLabel(simulcast);
  const platformLabel = SimulcastTransforms.getPlatformLabel(core.simulcastType);
  const detail = SimulcastTransforms.getDestinationDetail(simulcast);
  const error = SimulcastErrorUtils.getErrorState(simulcast, extPlatAuth, authsLoaded);
  const controlsDisabled = SimulcastErrorUtils.shouldDisableControls(error);

  const isStarted = core.simulcastStatusType === SimulcastStatusType.Started;
  const isPending = pendingAction !== null;
  const isSwitchPending = pendingAction === 'start' || pendingAction === 'stop';

  // Title and description overrides
  const hasTitle = core.title && core.title.trim().length > 0;
  const hasDesc = core.descShort && core.descShort.trim().length > 0;

  // Edit is disabled when error action is Delete (auth is missing/broken)
  const canEdit = error?.action !== SimulcastErrorAction.Delete;

  const handleSwitchToggle = () => {
    if (isStarted) {
      onStop(simulcast);
    } else {
      onStart(simulcast);
    }
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        {/* Header row: icon + label + switch + action menu */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <PlatformIcon type={core.simulcastType} />
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" truncate>
                {label}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {platformLabel}
                {detail && detail !== platformLabel ? ` \u00B7 ${detail}` : ''}
              </Text>
              <Text size="xs" c="dimmed" ff="monospace">
                ID: {bcLong.getStr(core.simulcastId)}
              </Text>
            </Stack>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {isSwitchPending && <Loader size={14} />}
            <Tooltip label={isStarted ? 'Stop' : 'Start'} withArrow>
              <Switch
                checked={isStarted}
                onChange={handleSwitchToggle}
                disabled={isPending || controlsDisabled}
                size="md"
              />
            </Tooltip>

            <Menu shadow="md" width={160} position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  disabled={isPending || !canEdit}
                  onClick={() => onEdit(simulcast)}
                >
                  Edit
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  disabled={isPending}
                  onClick={() => onDelete(simulcast)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {/* Error alert with action buttons */}
        {error && (
          <ErrorBanner
            error={error}
            simulcast={simulcast}
            pendingAction={pendingAction}
            onReconnect={onReconnect}
            onDelete={onDelete}
          />
        )}

        {/* Metadata rows (title/description overrides) */}
        {(hasTitle || hasDesc) && (
          <Stack gap={4}>
            {hasTitle && (
              <Text size="xs" c="dimmed">
                <Text span fw={500} c="dark">Title:</Text> {core.title}
              </Text>
            )}
            {hasDesc && (
              <Text size="xs" c="dimmed">
                <Text span fw={500} c="dark">Description:</Text> {core.descShort}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
